import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
  TextField,
  Checkbox,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import axios from "axios";
import { APP_API_V1_BASE } from "../../../config/api";
import { Close } from "@mui/icons-material";

const API = APP_API_V1_BASE;

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export default function AssignShiftModel({
  open,
  onClose,
  onSuccess,
  editData,
  isEdit,
}) {
  const [users, setUsers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [punching, setPunching] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [shiftId, setShiftId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [punch, setPunch] = useState("");
  const [loading, setLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [punchMode, setPunchMode] = useState("");
  const selectAllOption = {
    id: "all",
    name: "Select All",
  };

  const resetForm = () => {
    setSelectedUsers([]);
    setShiftId("");
    setLocationId("");
    setPunchMode("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  useEffect(() => {
    if (open) {
      fetchData();
      if (!isEdit) {
        resetForm();
      }
    }
  }, [open, isEdit]);

  useEffect(() => {
    if (isEdit && editData) {
      setSelectedUsers([
        {
          id: editData.user_id,
          name: editData.user_name,
          email: editData.email,
        },
      ]);

      setShiftId(editData.shift?.id || "");
      setLocationId(editData.attendance_location?.id || "");
      setPunchMode(editData.punch_mode || "");
    }
  }, [isEdit, editData]);
  const fetchData = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("authToken");

      const usersResponse = await axios.get(`${API}/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const shiftsResponse = await axios.get(`${API}/attendance/shifts`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const locationResponse = await axios.get(`${API}/attendance/location`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setUsers(usersResponse.data.data || []);
      setShifts(shiftsResponse.data || []);
      setLocations(locationResponse.data || []);
    } catch (error) {
      console.error("Fetch Data Error:", error);
      setUsers([]);
      setShifts([]);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!punchMode) {
      alert("Please select a Punch Mode.");
      return;
    }

    try {
      setAssignLoading(true);
      const token = localStorage.getItem("authToken");
      const payload = {
        id: editData?.id,
        user_id: editData?.user_id,
        user_ids: selectedUsers.map((user) => user.id),
        shift_id: shiftId,
        attendance_location_id: locationId,
        punch_mode: punchMode,
      };

      if (isEdit) {
        await axios.put(`${API}/attendance/edit/assignment`, payload, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
      } else {
        await axios.post(
          `${API}/attendance/shift/assign`,
          {
            user_ids: selectedUsers.map((user) => user.id),
            shift_id: shiftId,
            attendance_location_id: locationId,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );
        await axios.post(
          `${API}/attendance/punch/assign`,
          {
            user_ids: selectedUsers.map((user) => user.id),
            punch_mode: punchMode,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
      }

      resetForm();

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Assign Shift Error:", error);
    } finally {
      setAssignLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          }}
        >
          <Typography variant="h6" fontWeight={700} color="#fff">
            {isEdit ? "Edit Assignment" : "Assign Shift"}
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: "#fff" }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <>
          <FormControl fullWidth margin="normal">
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={[selectAllOption, ...users]}
              value={selectedUsers}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) =>
                option.id === "all"
                  ? "Select All"
                  : `${option.name} (${option.email})`
              }
              onChange={(event, newValue) => {
                const selectAll = newValue.find((item) => item.id === "all");

                if (selectAll) {
                  if (selectedUsers.length === users.length) {
                    setSelectedUsers([]);
                  } else {
                    setSelectedUsers(users);
                  }
                } else {
                  setSelectedUsers(newValue);
                }
              }}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    checked={
                      option.id === "all"
                        ? selectedUsers.length === users.length
                        : selected
                    }
                  />

                  {option.id === "all"
                    ? "Select All"
                    : `${option.name} (${option.email})`}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Users"
                  placeholder="Search users..."
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Shift</InputLabel>

            <Select
              value={shiftId}
              label="Shift"
              onChange={(e) => setShiftId(e.target.value)}
            >
              {shifts.map((shift) => (
                <MenuItem key={shift.id} value={shift.id}>
                  {shift.name} ({shift.start_time} - {shift.end_time})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Location</InputLabel>

            <Select
              value={locationId}
              label="Location"
              onChange={(e) => setLocationId(e.target.value)}
            >
              {locations.map((location) => (
                <MenuItem key={location.id} value={location.id}>
                  {location.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Punch Mode</InputLabel>

            <Select
              value={punchMode}
              label="Punch Mode"
              onChange={(e) => setPunchMode(e.target.value)}
            >
              <MenuItem value="after_clock_in">After Clock In</MenuItem>

              <MenuItem value="without_clock_in">Without Clock In</MenuItem>
            </Select>
          </FormControl>
        </>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>

        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={
            selectedUsers.length === 0 ||
            !shiftId ||
            !locationId ||
            !punchMode ||
            assignLoading
          }
        >
          {assignLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              {isEdit ? "Updating..." : "Assigning..."}
            </>
          ) : (
            isEdit ? "Update" : "Assign"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
